import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Section } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Gauge } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Design", "Video", "Web", "Content", "Strategy", "Motion", "Photography", "Other"];

const empty = { name: "", category: "Design", benchmark_per_unit: 1, description: "" };

export default function TaskBenchmarks() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [templates, setTemplates] = useState([]);
  const [adherence, setAdherence] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [t, a] = await Promise.all([
      api.get("/task-templates"),
      api.get("/reports/benchmark-adherence"),
    ]);
    setTemplates(t.data);
    setAdherence(a.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, category: t.category || "Other", benchmark_per_unit: t.benchmark_per_unit, description: t.description || "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, benchmark_per_unit: parseFloat(form.benchmark_per_unit) || 0 };
      if (editing) {
        await api.put(`/task-templates/${editing.id}`, payload);
        toast.success("Benchmark updated");
      } else {
        await api.post("/task-templates", payload);
        toast.success("Benchmark added");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this benchmark? Existing tasks linked to it will keep their hours but lose the link.")) return;
    try {
      await api.delete(`/task-templates/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const adherenceMap = Object.fromEntries(adherence.map(a => [a.template, a]));
  const groups = templates.reduce((acc, t) => {
    const k = t.category || "Other";
    acc[k] = acc[k] || [];
    acc[k].push(t);
    return acc;
  }, {});

  const adherenceColor = (pct, taskCount) => {
    if (taskCount === 0) return "text-neutral-400";
    if (pct >= 95) return "text-emerald-700";
    if (pct >= 75) return "text-amber-700";
    return "text-red-700";
  };

  return (
    <div data-testid="benchmarks-page">
      <PageHeader
        eyebrow="Settings"
        title="Task benchmarks"
        description="Define expected hours per unit for the work types you do most. Used to compute project efficiency in reports."
        testid="benchmarks-header"
        actions={isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="new-template-btn">
                <Plus size={14} className="mr-1" /> New benchmark
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md rounded-sm">
              <DialogHeader><DialogTitle>{editing ? "Edit benchmark" : "New benchmark"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Reel Edit" data-testid="template-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="template-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Hours / unit</Label>
                    <Input type="number" min="0" step="0.25" value={form.benchmark_per_unit} onChange={e => setForm({ ...form, benchmark_per_unit: e.target.value })} data-testid="template-bench-input" />
                  </div>
                </div>
                <div><Label>Description (optional)</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What's included in one unit?" data-testid="template-desc-input" /></div>
                <div className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 p-3 rounded-sm">
                  When a task uses this template, effective benchmark = <span className="font-mono">quantity × {parseFloat(form.benchmark_per_unit) || 0}h</span>.
                </div>
              </div>
              <DialogFooter>
                <Button onClick={save} disabled={!form.name} className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="template-save-btn">
                  {editing ? "Save changes" : "Create benchmark"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <Section>
        {templates.length === 0 ? (
          <div className="border border-dashed border-neutral-300 p-16 grid place-items-center text-center" data-testid="benchmarks-empty">
            <Gauge size={32} className="text-neutral-400 mb-3" />
            <div className="font-display text-xl font-bold mb-1">No benchmarks defined yet</div>
            <div className="text-sm text-neutral-500 mb-4 max-w-md">
              Add the work types your studio does often — Reel Edit, Logo Design, Landing Page, Blog Post — and the average time each takes per unit. Then everyone can pick from the same list when creating tasks.
            </div>
            {isAdmin && (
              <Button onClick={openCreate} className="rounded-sm bg-neutral-900 hover:bg-neutral-800" data-testid="empty-create-template">
                <Plus size={14} className="mr-1" /> Add first benchmark
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-px bg-neutral-200 border border-neutral-200">
            {Object.entries(groups).map(([cat, list]) => (
              <div key={cat} className="bg-white">
                <div className="px-5 py-3 border-b border-neutral-100 bg-neutral-50">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold">{cat}</div>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                      <th className="text-left px-5 py-2 font-semibold">Benchmark</th>
                      <th className="text-right px-5 py-2 font-semibold">Hours / unit</th>
                      <th className="text-right px-5 py-2 font-semibold">Tasks logged</th>
                      <th className="text-right px-5 py-2 font-semibold">Avg actual</th>
                      <th className="text-right px-5 py-2 font-semibold">Adherence</th>
                      {isAdmin && <th className="text-right px-5 py-2 font-semibold">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {list.map(t => {
                      const a = adherenceMap[t.name] || { task_count: 0, avg_actual_hours: 0, adherence_pct: 0 };
                      return (
                        <tr key={t.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`template-row-${t.id}`}>
                          <td className="px-5 py-3">
                            <div className="font-medium">{t.name}</div>
                            {t.description && <div className="text-xs text-neutral-500">{t.description}</div>}
                          </td>
                          <td className="px-5 py-3 text-right font-mono font-semibold">{t.benchmark_per_unit}h</td>
                          <td className="px-5 py-3 text-right font-mono text-neutral-600">{a.task_count}</td>
                          <td className="px-5 py-3 text-right font-mono text-neutral-600">{a.task_count > 0 ? `${a.avg_actual_hours}h` : "—"}</td>
                          <td className={`px-5 py-3 text-right font-mono font-semibold ${adherenceColor(a.adherence_pct, a.task_count)}`}>
                            {a.task_count > 0 ? `${a.adherence_pct}%` : "—"}
                          </td>
                          {isAdmin && (
                            <td className="px-5 py-3 text-right space-x-1">
                              <Button variant="ghost" size="sm" onClick={() => openEdit(t)} className="rounded-sm h-8" data-testid={`edit-template-${t.id}`}><Pencil size={13} /></Button>
                              <Button variant="ghost" size="sm" onClick={() => remove(t.id)} className="rounded-sm h-8 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-template-${t.id}`}><Trash2 size={13} /></Button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </Section>

      {templates.length > 0 && (
        <Section>
          <div className="border border-neutral-200 bg-neutral-50 p-5 max-w-3xl">
            <div className="flex items-start gap-3">
              <div className="w-1 h-full bg-neutral-900 self-stretch" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-1">How adherence works</div>
                <div className="text-sm text-neutral-700 leading-relaxed">
                  <span className="font-mono font-semibold">Adherence %</span> = benchmark hours ÷ average actual hours per unit · across all tasks linked to this benchmark.
                  <br />
                  <span className="text-emerald-700">≥ 95%</span> on track ·
                  <span className="text-amber-700"> 75–95%</span> running over ·
                  <span className="text-red-700"> &lt; 75%</span> significantly over benchmark.
                </div>
              </div>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}
